import { AdminWorkspaceContextData } from 'src/contracts/multiCompany/v1';

export type TenantWorkspaceRouteScope = Pick<
  AdminWorkspaceContextData,
  'company' | 'routeBase'
>;

export class TenantRouteScopeError extends Error {
  public readonly code = 'TENANT_ROUTE_SCOPE_MISMATCH';

  public constructor() {
    super('The requested workspace is unavailable.');
    this.name = 'TenantRouteScopeError';
  }
}

const positiveInteger = (value: unknown): number | null => {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && /^\d+$/.test(value)
      ? Number(value)
      : Number.NaN;
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
};

export const parsePlatformRouteCompanyId = (value: unknown): number | null =>
  positiveInteger(value);

const safeSuffix = (suffix: string): string => {
  const segments = suffix
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);
  if (
    segments.some(
      (segment) =>
        segment === '.' ||
        segment === '..' ||
        !/^[A-Za-z0-9_.~-]+$/.test(segment)
    )
  ) {
    throw new TenantRouteScopeError();
  }
  return segments.length > 0 ? `/${segments.join('/')}` : '';
};

export const assertCompanyWorkspaceRoute = (
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown
): void => {
  const companyId = positiveInteger(routeCompanyId);
  if (companyId === null || companyId !== workspace.company.id) {
    throw new TenantRouteScopeError();
  }
};

export const companyWorkspacePath = (
  workspace: TenantWorkspaceRouteScope,
  suffix = ''
): string => `${workspace.routeBase}${safeSuffix(suffix)}`;

export const companyWorkersPath = (
  workspace: TenantWorkspaceRouteScope &
    Pick<AdminWorkspaceContextData, 'role' | 'accessLevel'>
): string => {
  if ((workspace.accessLevel ?? workspace.role) === 'COMPANY_ADMIN') {
    return companyWorkspacePath(workspace, 'workers');
  }
  return `${companyWorkspacePath(
    workspace,
    'profile/data/workers'
  )}?scope=mine`;
};

export const companyTenantApiPath = (
  workspace: TenantWorkspaceRouteScope,
  routeCompanyId: unknown,
  resource: string
): string => {
  assertCompanyWorkspaceRoute(workspace, routeCompanyId);
  return `${workspace.routeBase}${safeSuffix(resource)}`;
};

/**
 * Super Admin stays on an explicit platform route and never reuses Company
 * Admin workspace state.
 */
export const platformTenantPath = (companyId: unknown, suffix = ''): string => {
  const parsed = parsePlatformRouteCompanyId(companyId);
  if (parsed === null) throw new TenantRouteScopeError();
  return `/platform/companies/${parsed}${safeSuffix(suffix)}`;
};
